import { type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { SpaceType } from '@/constants/space';
import { TodayAvailabilityLoungeItem } from '@/components/elements/TodayAvailabilityLoungeItem';
import { AIRPORTS, TERMINALS, LOUNGES, FLIGHT_TYPES } from '@/constants/area';
import { SPACE_TYPE } from '@/constants/space';
import { STATUS_ICONS } from '@/constants/todayAvailability';

// シャワールーム用: 4つのラウンジの検索条件（国際線）
const SHOWER_ROOM_LOUNGE_QUERIES = [
  {
    airport: AIRPORTS.HANEDA,
    terminal: TERMINALS.TERMINAL2,
    lounge: LOUNGES.SUITE_LOUNGE,
    flightType: FLIGHT_TYPES.INTERNATIONAL,
  },
  {
    airport: AIRPORTS.HANEDA,
    terminal: TERMINALS.TERMINAL2,
    lounge: LOUNGES.ANA_LOUNGE,
    flightType: FLIGHT_TYPES.INTERNATIONAL,
  },
  {
    airport: AIRPORTS.HANEDA,
    terminal: TERMINALS.TERMINAL3,
    lounge: LOUNGES.SUITE_LOUNGE,
    flightType: FLIGHT_TYPES.INTERNATIONAL,
  },
  {
    airport: AIRPORTS.HANEDA,
    terminal: TERMINALS.TERMINAL3,
    lounge: LOUNGES.ANA_LOUNGE,
    flightType: FLIGHT_TYPES.INTERNATIONAL,
  },
] as const;

// 防音個室ブース用: 羽田空港 第2ターミナル 国内線 ANA SUITE LOUNGE のみ
const PRIVATE_ROOM_LOUNGE_QUERIES = [
  {
    airport: AIRPORTS.HANEDA,
    terminal: TERMINALS.TERMINAL2,
    lounge: LOUNGES.SUITE_LOUNGE,
    flightType: FLIGHT_TYPES.DOMESTIC,
  },
] as const;

interface TodayAvailabilityModalProps {
  dialogRef: RefObject<HTMLDialogElement | null>;
  onClose: () => void;
  spaceType: SpaceType | null;
}

export const TodayAvailabilityModal = ({
  dialogRef,
  onClose,
  spaceType,
}: TodayAvailabilityModalProps) => {
  const { t } = useTranslation();

  // スペースタイプに応じた凡例キーとラウンジクエリを取得
  const legendKey =
    spaceType === SPACE_TYPE.PRIVATE_ROOM ? 'privateRoom' : 'showerRoom';
  const loungeQueries =
    spaceType === SPACE_TYPE.PRIVATE_ROOM
      ? PRIVATE_ROOM_LOUNGE_QUERIES
      : SHOWER_ROOM_LOUNGE_QUERIES;

  return (
    <dialog className="dialog dialog--vacant" ref={dialogRef}>
      <div className="dialog__line dialog__line--vacant">
        <div className="dialog__area dialog__area--vacant">
          <button
            className="dialog__btn--close dialog__btn--close-vacant"
            type="button"
            aria-label={t('common.back')}
            onClick={onClose}
          >
            ×
          </button>
          <div className="dialog__content dialog__content--vacant">
            {/* 凡例 */}
            <div className="dialog__legend">
              <table className="dialog__legend-table">
                <tbody>
                  <tr>
                    <td className="dialog__legend-mark">
                      <img src={STATUS_ICONS.available} alt="○" />
                    </td>
                    <td className="dialog__legend-text">
                      {t(`todayAvailability.legend.${legendKey}.available`)}
                    </td>
                  </tr>
                  <tr>
                    <td className="dialog__legend-mark">
                      <img src={STATUS_ICONS.limited} alt="△" />
                    </td>
                    <td className="dialog__legend-text">
                      {t(`todayAvailability.legend.${legendKey}.limited`)}
                    </td>
                  </tr>
                  <tr>
                    <td className="dialog__legend-mark">
                      <img src={STATUS_ICONS.full} alt="×" />
                    </td>
                    <td className="dialog__legend-text">
                      {t(`todayAvailability.legend.${legendKey}.full`)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 各ラウンジを独立して表示（それぞれがAPIを呼ぶ） */}
            <div className="dialog__list">
              {spaceType &&
                loungeQueries.map((query) => (
                  <TodayAvailabilityLoungeItem
                    key={`${query.airport}-${query.terminal}-${query.lounge}`}
                    airport={query.airport}
                    terminal={query.terminal}
                    lounge={query.lounge}
                    spaceType={spaceType}
                    flightType={query.flightType}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
};
